'use client';

import React from "react";
import "./learnerprivacy.modules.css";
import Footer from "@components/footer/Footer";
import Navbar from "@components/Navbar";

const ChildrensPrivacyNotice = () => {
  return (
    <>
      <Navbar />
      <div className="container">
        {/* Header Section */}
        <header>
          <h1>Children&apos;s Privacy Notice</h1>
          <p>Last updated December 17, 2025</p>
        </header>

        {/* Main Content */}
        <main className="content">
          <p>
            Our <a href="/footer/privacy-policy">Privacy Policy</a> explains how we,{" "}
            <strong>PenguinLearn Holdings LLC, doing business as PenguinLearn</strong> (“PenguinLearn,” “we,”
            “us,” “our”), collect and use personal information when you use our online services (such as
            classes and other learning activities).
            {" "}
            This Children’s Privacy Notice (“Notice”) specifically addresses the U.S. Children&apos;s Online
            Privacy Protection Act (“COPPA”). For COPPA purposes, “Children” means kids under 13 years old.
          </p>

          <p>
            For more information about COPPA and general tips about protecting children’s online privacy,
            please visit the U.S. Federal Trade Commission’s (FTC&apos;s) consumer information page at{" "}
            <a
              href="https://www.ftc.gov/business-guidance/privacy-security/childrens-privacy"
              target="_blank"
              rel="noreferrer"
            >
              https://www.ftc.gov/business-guidance/privacy-security/childrens-privacy
            </a>
            .
          </p>

          <p>
            PenguinLearn is an education marketplace where Parents / Guardians create accounts and enroll
            Learners in live online classes taught by independent Teachers. We collect most personal
            information about Children from Parents / Guardians, but we also collect some information
            directly from Children when they participate in PenguinLearn.
          </p>

          <p>
            We do not allow Children to sign up for a full PenguinLearn account or enroll in paid classes
            on their own. Parents / Guardians control enrollment and account access for Children.
          </p>

          <p>
            PenguinLearn uses <strong>Zoom</strong> for live classes, and <strong>classes are recorded</strong>.
          </p>

          <h2>How we collect information from Children</h2>
          <p>
            <strong>1) Information provided by Parents / Guardians.</strong> Parents / Guardians create the
            Parent account and create Learner profiles for their Child. Parents may provide the Child’s
            name (or display name) and age (or age range), and may optionally provide other information
            relevant to learning (such as time zone or learning preferences).
          </p>

          <p>
            <strong>2) Information provided by Children during use.</strong> Once a Learner profile is created,
            Children may provide information through participation in classes and activities, such as chat
            messages during class, responses to the Teacher, or submitted work (if a class includes it).
          </p>

          <p>
            <strong>3) Limited consent accounts.</strong> In some cases, a Child may attempt to sign up directly.
            If that happens, we limit what the Child can do until a Parent / Guardian completes account setup
            and consent steps. See “Limited consent accounts” below.
          </p>

          <h2>Learner Mode and account restrictions</h2>
          <p>
            Children should only use the Learner experience that is connected to a Parent / Guardian account.
            Children must use that Learner experience to attend classes and participate in learning activities.
          </p>

          <p>
            Children cannot make purchases or enroll themselves in paid classes. Parents / Guardians control
            enrollment and payments.
          </p>

          <p>
            Parents / Guardians may receive service-related emails about their Child’s enrollments and class
            activity (for example, confirmations and class updates), and Parents can unenroll their Child
            at any time.
          </p>

          <h2>What can Children do on PenguinLearn?</h2>
          <p>
            Children participate in live classes and may interact with the Teacher and other learners during
            the session through video/audio, chat, or other class tools. Children may also participate in
            optional activities that the Parent has enabled.
          </p>

          <p>
            We may use limited engagement signals (for example, a class a Child completed or saved) to help
            Parents find relevant classes. We do not send marketing emails to Children.
          </p>

          <p>
            Our classes and activities take place over live video sessions. Video images and audio of Children
            may be captured during live sessions, and <strong>classes are recorded</strong>, along with chat
            messages and any content shared on screen during the session.
          </p>

          <h2>Verified accounts and parental consent</h2>
          <p>
            Before a Child can fully participate in PenguinLearn, a Parent / Guardian must create the Parent
            account and create the Child’s Learner profile. Where COPPA requires verifiable parental consent,
            we obtain parental consent before allowing collection, use, and sharing of a Child’s personal
            information beyond what is permitted under COPPA without consent.
          </p>

          <p>
            Children may receive essential service notifications related to participation (for example, reminders
            that a class is starting) depending on how a Parent configures the account. We do not send marketing
            emails to Children.
          </p>

          <h2>Information is shared with</h2>
          <p><strong>Teachers</strong> may access information necessary to run the class, such as:</p>
          <ul>
            <li>Child display name and age/age range (as needed for class context)</li>
            <li>Class participation details (such as attendance)</li>
            <li>Child communications to the Teacher inside the class experience</li>
            <li>Class recordings (see “Video recordings” below)</li>
          </ul>

          <p>
            <strong>Other Learners in the same class</strong> may see or hear, depending on the class format:
          </p>
          <ul>
            <li>Video and audio during live sessions (and content visible in the background)</li>
            <li>Chat messages or other in-session communications</li>
          </ul>

          <p>
            PenguinLearn staff and contractors may review information (including recordings and messages) for
            safety, customer support, moderation, compliance, enforcement, and platform operations.
          </p>

          <h2>Video recordings</h2>
          <p>
            We record video, audio, and written communications during classes for safety, quality assurance,
            dispute resolution, and support. Recordings may be available to the Teacher and may be shared with
            enrolled families for that class to view.
          </p>

          <p>
            We will obtain additional parental consent before using recordings for promotional or other
            unexpected purposes.
          </p>

          <p>
            We require Teachers to follow our rules about recordings and to not share recordings outside the
            platform except as permitted. However, we cannot fully control what other class participants do
            once they have access to a recording.
          </p>

          <h2>Third-party sites and tools</h2>
          <p>
            Some Teachers may ask Learners and Parents to use third-party sites or tools as part of a class.
            Parents should review the privacy notices for those third-party services. These third parties are
            not PenguinLearn service providers, and PenguinLearn is not responsible for their practices.
          </p>

          <h2>Limited consent accounts</h2>
          <p>
            If a Child attempts to sign up independently, we limit the account until a Parent / Guardian
            completes the required steps. During this limited state, we restrict participation so that the
            Child cannot enroll in paid classes and the Child’s information is not shared with Teachers or
            other Learners beyond what is necessary to run the limited experience.
          </p>

          <p>
            Parents may upgrade the account by completing the Parent account setup and any required consent
            steps. If you do not want information from the limited state carried over, contact us at{" "}
            <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>.
          </p>

          <h2>Parental rights</h2>
          <p>
            Parents / Guardians have the right to review the personal information we have collected from their
            Child, request deletion of that information, and refuse further collection or use of their Child’s
            information.
          </p>

          <p>
            Parents / Guardians can make these requests by contacting us at{" "}
            <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>.
            We may take steps to verify that the request is coming from a Parent / Guardian.
          </p>

          <h2>No consent / revoke consent</h2>
          <p>
            If Parents / Guardians do not provide required consent (or withdraw consent), we will stop collecting,
            using, and sharing the Child’s personal information except as necessary for legal compliance and
            enforcement. The Child will not be able to participate in PenguinLearn classes or activities.
          </p>

          <h2>Deletion and retention</h2>
          <p>
            Parents / Guardians may request deletion of the personal information we collected in connection
            with their Child’s account. Please note that deleting records may result in termination of access
            to classes, recordings, or other services tied to the account.
          </p>

          <p>
            We may retain certain information where required or permitted for safety, fraud prevention,
            compliance, dispute resolution, or other legitimate operational needs. We will continue to protect
            retained information and delete it when it is no longer needed.
          </p>

          <p>
            We will use commercially reasonable efforts to delete Children’s personal information when it is
            no longer needed for the purpose for which it was collected, consistent with applicable law.
          </p>

          <h2>Review and update information</h2>
          <p>
            Parents / Guardians may review and update certain Child profile information by logging into their
            Parent account. If Parents want us to help with updates or corrections, they can email{" "}
            <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>.
          </p>

          <h2>About Teachers</h2>
          <p>
            Teachers on PenguinLearn are independent contractors. Teachers are required to complete a{" "}
            <strong>background check</strong> as part of onboarding and/or ongoing safety requirements.
          </p>

          <p>
            PenguinLearn staff may also monitor or moderate certain classes or activities for safety and
            compliance.
          </p>

          <h2>Contact us</h2>
          <p>
            If you have questions or concerns about this Children’s Privacy Notice or COPPA, contact us at:
          </p>
          <ul>
            <li>
              Email: <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>
            </li>
          </ul>
        </main>
      </div>
      <Footer />
    </>
  );
};

export default ChildrensPrivacyNotice;